import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { Search, Filter, Plus, MapPin, Calendar, Download, Info, MessageCircle } from 'lucide-react';
import {
  fetchCandidates,
  fetchTags,
  type CandidateFilters,
  type CandidateListStatus,
} from '@/lib/api';
import { Candidate } from '@/types';
import { getInitials, formatStatusLabel } from '@/lib/utils';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BulkActionsBar } from '@/components/bulk/BulkActionsBar';
import { ExportDialog } from '@/components/export/ExportDialog';
import { DataQualityBadge, humaniseField } from '@/components/candidates/DataQualityBadge';
import { useToast } from '@/contexts/ToastContext';

const AVAILABILITY_OPTIONS = [
  { value: 'all', label: 'All Availability' },
  { value: 'immediate', label: 'Immediate' },
  { value: '2_weeks', label: '2 Weeks' },
  { value: '1_month', label: '1 Month' },
  { value: '2_months', label: '2 Months' },
  { value: '3_months+', label: '3+ Months' },
];

function getStatusBadgeVariant(
  status: string,
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'completed':
    case 'complete':
      return 'success';
    case 'in_progress':
      return 'default';
    case 'stalled':
      return 'warning';
    case 'new':
    default:
      return 'secondary';
  }
}

/**
 * Build a compact human-readable summary of missing required fields for the
 * In-Progress tab. Example: "Missing required: email, ID number, 2 more".
 */
function formatMissingSummary(missing: string[] | undefined): string | null {
  if (!missing || missing.length === 0) return null;
  const MAX_INLINE = 2;
  const inline = missing.slice(0, MAX_INLINE).map(humaniseField);
  const remaining = missing.length - MAX_INLINE;
  const parts = [...inline];
  if (remaining > 0) {
    parts.push(`${remaining} more field${remaining === 1 ? '' : 's'}`);
  }
  return `Missing required: ${parts.join(', ')}`;
}

export function CandidatesPage() {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<CandidateListStatus>('completed');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [availability, setAvailability] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [exportOpen, setExportOpen] = useState(false);
  const pageSize = 10;

  const isInProgress = activeTab === 'in_progress';

  const { data: tags = [] } = useQuery({
    queryKey: ['tags'],
    queryFn: fetchTags,
  });

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Simple debounce using setTimeout
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timeoutId);
  };

  const filters: CandidateFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      status: activeTab,
      availability: availability !== 'all' ? availability : undefined,
      tagId: selectedTag !== 'all' ? selectedTag : undefined,
      page,
      limit: pageSize,
    }),
    [debouncedSearch, activeTab, availability, selectedTag, page],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['candidates', filters],
    queryFn: () => fetchCandidates(filters),
  });

  const handleTabChange = (value: string) => {
    const next = value as CandidateListStatus;
    setActiveTab(next);
    setPage(1);
    setRowSelection({}); // Clear selection when switching tabs — drafts aren't bulk-operable.
  };

  const handleResumeOnboarding = (candidate: Candidate) => {
    // Stub for now — real implementation will trigger a WhatsApp-agent nudge
    // once Phase 2 ships. See plan: polymorphic-tickling-fairy.md.
    addToast({
      type: 'info',
      title: 'Nudge sent — coming soon',
      message: `Follow-up for ${candidate.firstName} ${candidate.lastName} will be enabled once the WhatsApp agent upgrade ships.`,
    });
  };

  const columns: ColumnDef<Candidate>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => {
          const candidate = row.original;
          const missingSummary = isInProgress
            ? formatMissingSummary(candidate.missing_fields)
            : null;
          return (
            <div className="flex items-center gap-3">
              <Link
                to={`/candidates/${candidate.id}`}
                className="flex items-center gap-3 hover:text-emerald-500"
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback>
                    {getInitials(candidate.firstName, candidate.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-medium">
                    {candidate.firstName} {candidate.lastName}
                  </div>
                  {candidate.currentJobTitle && (
                    <div className="text-sm text-text-tertiary">
                      {candidate.currentJobTitle}
                    </div>
                  )}
                  {missingSummary && (
                    <div className="text-xs text-amber-400 mt-0.5">{missingSummary}</div>
                  )}
                </div>
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: 'whatsappPhone',
        header: 'Contact',
        cell: ({ row }) => {
          const candidate = row.original;
          return (
            <div>
              <div className="text-sm">{candidate.whatsappPhone}</div>
              {candidate.email && (
                <div className="text-sm text-text-tertiary">{candidate.email}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'currentLocation',
        header: 'Location',
        cell: ({ row }) => {
          const location = row.original.currentLocation;
          if (!location) return <span className="text-text-tertiary">-</span>;
          return (
            <div className="flex items-center gap-1 text-sm">
              <MapPin className="h-3.5 w-3.5 text-text-tertiary" />
              {location}
            </div>
          );
        },
      },
      {
        accessorKey: 'skills',
        header: 'Skills',
        cell: ({ row }) => {
          const skills = row.original.skills;
          if (!skills || skills.length === 0) {
            return <span className="text-text-tertiary">-</span>;
          }
          const displaySkills = skills.slice(0, 3);
          const remaining = skills.length - 3;
          return (
            <div className="flex flex-wrap gap-1">
              {displaySkills.map((skill) => (
                <Badge key={skill} variant="secondary" className="text-xs">
                  {skill}
                </Badge>
              ))}
              {remaining > 0 && (
                <Badge variant="outline" className="text-xs">
                  +{remaining}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'data_quality_score',
        header: 'Quality',
        cell: ({ row }) => (
          <DataQualityBadge
            score={row.original.data_quality_score}
            missingFields={row.original.missing_fields}
          />
        ),
      },
      {
        accessorKey: 'tags',
        header: 'Tags',
        cell: ({ row }) => {
          const candidateTags = row.original.tags;
          if (!candidateTags || candidateTags.length === 0) {
            return <span className="text-text-tertiary">-</span>;
          }
          const displayTags = candidateTags.slice(0, 3);
          const remaining = candidateTags.length - 3;
          return (
            <div className="flex flex-wrap gap-1">
              {displayTags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="text-xs"
                  style={{ borderColor: tag.color, color: tag.color }}
                >
                  {tag.name}
                </Badge>
              ))}
              {remaining > 0 && (
                <Badge variant="outline" className="text-xs">
                  +{remaining}
                </Badge>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'availabilityDate',
        header: 'Availability',
        cell: ({ row }) => {
          const date = row.original.availabilityDate;
          if (!date) return <span className="text-text-tertiary">-</span>;
          return (
            <div className="flex items-center gap-1 text-sm">
              <Calendar className="h-3.5 w-3.5 text-text-tertiary" />
              {new Date(date).toLocaleDateString()}
            </div>
          );
        },
      },
      {
        accessorKey: 'onboardingStatus',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.onboardingStatus;
          return (
            <Badge variant={getStatusBadgeVariant(status)}>
              {formatStatusLabel(status)}
            </Badge>
          );
        },
      },
      // Final column only rendered on the In-Progress tab: a stub action to
      // prompt the WhatsApp agent to nudge the candidate back into onboarding.
      ...(isInProgress
        ? [
            {
              id: 'resume',
              header: '',
              cell: ({ row }: { row: { original: Candidate } }) => (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleResumeOnboarding(row.original)}
                >
                  <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                  Resume onboarding
                </Button>
              ),
            } as ColumnDef<Candidate>,
          ]
        : []),
    ],
    [isInProgress], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleFilterChange = (
    filterType: 'availability' | 'tag',
    value: string,
  ) => {
    if (filterType === 'availability') {
      setAvailability(value);
    } else if (filterType === 'tag') {
      setSelectedTag(value);
    }
    setPage(1); // Reset to first page on filter change
  };

  const completedCount = data?.completedCount ?? 0;
  const inProgressCount = data?.inProgressCount ?? 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Candidates</h1>
          <p className="text-text-secondary mt-1">
            Manage and track your candidate pipeline
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setExportOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button asChild>
            <Link to="/candidates/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Candidate
            </Link>
          </Button>
        </div>
      </div>

      {/* Tabs: Completed (default) / In Progress */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="completed">
            Completed
            <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-xs text-text-secondary">
              {completedCount}
            </span>
          </TabsTrigger>
          <TabsTrigger value="in_progress">
            In Progress
            <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-xs text-text-secondary">
              {inProgressCount}
            </span>
          </TabsTrigger>
        </TabsList>

        {isInProgress && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
            <Info className="h-5 w-5 flex-shrink-0 text-amber-400" />
            <div>
              In-progress candidates are excluded from matching, pipeline, search,
              export, bulk actions, and AI tools until every required profile field
              is completed.
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-4 bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
              <Input
                placeholder="Search by name, email, phone, or skills..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-text-tertiary" />
              </div>

              <Select
                value={availability}
                onValueChange={(value) => handleFilterChange('availability', value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Availability" />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABILITY_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedTag}
                onValueChange={(value) => handleFilterChange('tag', value)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tags</SelectItem>
                  {tags.map((tag) => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Table content — shared shell rendered per-tab via TabsContent so
            a11y tab semantics stay correct. Same table, different filters. */}
        <TabsContent value="completed" className="mt-4">
          <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              page={page}
              pageSize={pageSize}
              total={data?.pagination.total ?? 0}
              onPageChange={setPage}
              isLoading={isLoading}
              emptyMessage="No candidates found. Try adjusting your filters."
              getRowId={(row) => row.id}
              enableRowSelection
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
            />
          </div>
        </TabsContent>

        <TabsContent value="in_progress" className="mt-4">
          <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
            <DataTable
              columns={columns}
              data={data?.data ?? []}
              page={page}
              pageSize={pageSize}
              total={data?.pagination.total ?? 0}
              onPageChange={setPage}
              isLoading={isLoading}
              emptyMessage="No in-progress candidates. Completed profiles appear in the other tab."
              getRowId={(row) => row.id}
              /* Bulk ops intentionally disabled on drafts — the backend also
                 refuses bulk mutations for in_progress rows (Phase 1). */
              enableRowSelection={false}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* Bulk Actions — only relevant on Completed tab */}
      {!isInProgress && Object.keys(rowSelection).length > 0 && (
        <BulkActionsBar
          selectedIds={Object.keys(rowSelection)}
          onClear={() => setRowSelection({})}
          onRefresh={() => setRowSelection({})}
        />
      )}

      {/* Export Dialog */}
      <ExportDialog open={exportOpen} onOpenChange={setExportOpen} entity="candidates" />
    </div>
  );
}
